import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Trash2, Play, Pause, X } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useState } from "react";

interface BulkActionsProps {
  selectedTests: string[];
  onClearSelection: () => void;
  onBulkDelete: (testIds: string[]) => Promise<void>;
  onBulkActivate: (testIds: string[]) => Promise<void>;
  onBulkDeactivate: (testIds: string[]) => Promise<void>;
}

export const BulkActions = ({
  selectedTests,
  onClearSelection,
  onBulkDelete,
  onBulkActivate,
  onBulkDeactivate
}: BulkActionsProps) => {
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  if (selectedTests.length === 0) return null;

  const handleBulkDelete = async () => {
    setIsLoading(true);
    try {
      await onBulkDelete(selectedTests);
      setShowDeleteDialog(false);
      onClearSelection();
    } catch (error) {
      console.error("Bulk delete failed:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBulkAction = async (action: () => Promise<void>) => {
    setIsLoading(true);
    try {
      await action();
      onClearSelection();
    } catch (error) {
      console.error("Bulk action failed:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="bg-accent/50 border border-border rounded-lg p-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Badge variant="secondary" className="px-2 py-1">
              {selectedTests.length} selected
            </Badge>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClearSelection}
              className="h-6 w-6 p-0"
            >
              <X className="h-3 w-3" />
            </Button>
          </div>
          
          <div className="h-4 w-px bg-border" />
          
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleBulkAction(() => onBulkActivate(selectedTests))}
              disabled={isLoading}
              className="border-border"
            >
              <Play className="h-4 w-4 mr-2" />
              Activate
            </Button>
            
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleBulkAction(() => onBulkDeactivate(selectedTests))}
              disabled={isLoading}
              className="border-border"
            >
              <Pause className="h-4 w-4 mr-2" />
              Deactivate
            </Button>
            
          </div>
        </div>
        
        <Button
          variant="destructive"
          size="sm"
          onClick={() => setShowDeleteDialog(true)}
          disabled={isLoading}
        >
          <Trash2 className="h-4 w-4 mr-2" />
          Delete Selected
        </Button>
      </div>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Selected Tests</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {selectedTests.length} test{selectedTests.length > 1 ? 's' : ''}? 
              This action cannot be undone and will permanently delete the test{selectedTests.length > 1 ? 's' : ''} 
              and all associated data including responses and results.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBulkDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={isLoading}
            >
              {isLoading ? "Deleting..." : "Delete Tests"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};